import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classnames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import { useClickOutside } from '../../../../../../providers/useClickOutside/useClickOutside';
import styles from '../button.scss';
import StylesContext from '../../../StylesContext';
import semanticClassNames from '../../../../RichTextBox.semanticClassNames';
import AlignButton from './AlignButton';
import type { AlignButtonType } from './common';
import {
  alignmentButtons,
  getActiveAlignment,
  preventBubblingUp,
  getStateWithNewTextAlign,
  alignTypes,
} from './common';
import type { ExtendedToolbarChildrenProps } from '../../types';
import Tooltip from '../Tooltip';
import { useActionTranslations } from '../../../useActionTranslations';

const InlineTextAlignButton = forwardRef<
  HTMLButtonElement,
  ExtendedToolbarChildrenProps
>((props, ref) => {
  const { setEditorState, getEditorState, rootId, translations } = props;

  const [showOptions, setShowOptions] = useState(false);
  const {
    toolbarAlignLeftAction,
    toolbarAlignRightAction,
    toolbarAlignJustifyAction,
    toolbarAlignCenterAction,
  } = useActionTranslations(translations);

  const leftAlignRef = useRef<SVGSVGElement>(null);
  const centerAlignRef = useRef<SVGSVGElement>(null);
  const rightAlignRef = useRef<SVGSVGElement>(null);
  const justifyAlignRef = useRef<SVGSVGElement>(null);

  const alignActions: Record<AlignButtonType, string> = {
    left: toolbarAlignLeftAction,
    right: toolbarAlignRightAction,
    center: toolbarAlignCenterAction,
    justify: toolbarAlignJustifyAction,
  };

  const alignSelected: Record<AlignButtonType, string> = {
    left: translations.alignLeftSelected,
    right: translations.alignRightSelected,
    center: translations.alignCenterSelected,
    justify: translations.alignJustifySelected,
  };

  const hideOptions = () => {
    setShowOptions(false);
  };

  const stylesContext = useContext(StylesContext);

  useClickOutside(
    [
      ref as React.RefObject<HTMLElement>,
      leftAlignRef,
      centerAlignRef,
      rightAlignRef,
      justifyAlignRef,
    ],
    hideOptions,
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      if (ref && 'current' in ref) {
        ref.current?.focus();
      }
      setShowOptions(false);
    }
  };

  useEffect(() => {
    if (showOptions) {
      leftAlignRef.current?.focus();
    }
  }, [showOptions]);

  const alignRefs = {
    left: leftAlignRef,
    right: rightAlignRef,
    center: centerAlignRef,
    justify: justifyAlignRef,
  };

  const changeTextAlign = (
    e: React.MouseEvent<HTMLOrSVGElement>,
    newAlignment: AlignButtonType,
  ) => {
    preventBubblingUp(e);

    const newState = getStateWithNewTextAlign(newAlignment, getEditorState());
    hideOptions();
    setEditorState(newState);
  };

  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    preventBubblingUp(event);
    setShowOptions(true);
  };

  const activeAlignment = getActiveAlignment(getEditorState());
  return (
    <div
      onKeyDown={onKeyDown}
      className={styles.buttonContainer}
      onMouseDown={preventBubblingUp}
    >
      <Tooltip
        shouldShow={props.shouldShowTooltips}
        content={props.translations.toolbarAlign}
      >
        <button
          ref={ref}
          className={classnames(styles.button, {
            [styles.hovered]: stylesContext.hovered,
            [styles.focused]: stylesContext.focused,
            [styles.error]: stylesContext.error,
          })}
          onClick={onClickHandler}
          type="button"
          aria-haspopup="true"
          aria-expanded={showOptions}
          aria-controls={`alignMenu_${rootId}`}
          aria-label={`${translations.toolbarAlign}: ${alignSelected[activeAlignment]}`}
          onMouseDown={e => e.preventDefault()}
        >
          <div
            className={classnames(
              styles.buttonContent,
              formatClassNames(semanticClassNames.icons),
            )}
          >
            <AlignButton tabIndex={-1}>
              {alignmentButtons[activeAlignment]}
            </AlignButton>
          </div>
        </button>
      </Tooltip>
      {showOptions && (
        <div
          role="menu"
          id={`alignMenu_${rootId}`}
          className={classnames(
            styles.menuContainer,
            {
              [styles.hovered]: stylesContext.hovered,
              [styles.focused]: stylesContext.focused,
              [styles.error]: stylesContext.error,
            },
            styles.inline,
          )}
        >
          {alignTypes.map(alignType => {
            return (
              <Tooltip
                shouldShow={props.shouldShowTooltips}
                content={alignActions[alignType]}
                key={`tooltip_text_align_${alignType}`}
                position="right"
              >
                <button
                  onClick={e => changeTextAlign(e, alignType)}
                  onMouseDown={e => e.preventDefault()}
                >
                  <AlignButton
                    selected={activeAlignment === alignType}
                    ref={alignRefs[alignType]}
                  >
                    {alignmentButtons[alignType]}
                  </AlignButton>
                </button>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default InlineTextAlignButton;
